import { isAccessToken, OktaAuth } from '@okta/okta-auth-js';

import { sendSentryError } from './utils/sendSentryError';

const issuer = process.env.OKTA_ISSUER || '';
const clientId = process.env.OKTA_CLIENT_ID || '';
const REFRESH_EARLY_SECONDS = 20 * 60; // 20 minutes in seconds

const oktaAuth: OktaAuth = new OktaAuth({
  issuer,
  clientId,
  redirectUri: `${window.location.origin}/login`, // Redirects after login
  scopes: ['openid', 'profile', 'email', 'offline_access', 'idx_instancepermissions'], // Required scopes
  pkce: true, // Enables PKCE
  tokenManager: {
    storage: 'localStorage',
    storageKey: 'opal-app-okta-tokens',
  },
});

/**
 * Gets or renews the Okta access token if it's expiring within 20 minutes
 * @returns Promise<string> The access token (renewed if necessary)
 * @throws Error if unable to renew token or no token exists
 */
export const getOrRenewOptiIdToken = async (): Promise<string> => {
  try {
    const tokenManager = oktaAuth.tokenManager;
    const accessToken = await tokenManager.get('accessToken');

    if (!isAccessToken(accessToken)) {
      throw new Error('No access token found');
    }

    // Check if token expires within 20 minutes (1200 seconds)
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = accessToken.expiresAt || 0;
    const timeUntilExpiry = expiresAt - currentTime;

    if (timeUntilExpiry <= REFRESH_EARLY_SECONDS) {
      // Token is expiring within threshold minutes, renew it
      const renewedToken = await tokenManager.renew('accessToken');
      if (!isAccessToken(renewedToken)) {
        throw new Error('No access token found during renewal');
      }
      return renewedToken.accessToken;
    }
    // Token is still valid for more than threshold minutes
    return accessToken.accessToken;
  } catch (error) {
    const errorMessage = `Failed to renew access token: ${
      error instanceof Error ? error.message : 'Unknown error'
    }`;
    // Send error to Sentry before throwing
    sendSentryError(error instanceof Error ? error : new Error(errorMessage), {
      context: 'getOrRenewOptiIdToken',
      operation: 'token_renewal',
    });

    throw new Error(errorMessage);
  }
};

export default oktaAuth;
